// OwnedProductsTable — the Products section's "Hardware & one-time" tab
// (account-area redesign Task 7, Amendment 2026-10-02 F6/F16 + ruling R4).
//
// One EmbedTable row per ONE-TIME owned item (`kind: "one_time"`, one per
// product; the server decides the rows and ProductsTab hands them over as
// `rows`). Per row:
//   - Product (primary): the thumbnail from the item's `product.image_url` when
//     it carries one, and NO square at all when it doesn't (amendment: the old
//     grey placeholder box is retired); the name, the type badge and the item's
//     service-option badges.
//   - Owned: the units owned and, for a member who manages stores, the
//     per-store split plus the Move button (TD-671 Task 8/17 — the per-row
//     store select is gone).
//   - Buy again and Details: each its own cell.
//   - Status: the latest return request as an rma badge (missing degrades to
//     the neutral em dash per EmbedStatusBadge's contract).
//   - Actions: the ⋯ menu — ONLY "Request a return". Cancelling a subscription
//     is not here: it is the "Cancel subscription" link in the Details dialog
//     (R9) and Resume is a visible row link on subscription rows. The menu is
//     hidden entirely while an OPEN return request exists.
//
// This file also exports ProductRowActions, the shared ⋯ cell —
// SubscriptionsTable renders it too (a hardware product with a subscription
// row keeps its return affordance there).
//
// Anti-corruption note (unchanged from this module's origins): rows arrive
// pre-joined by ProductsTab; this component holds no fetch logic.
//
// Inline styles only — no Tailwind.
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 8). ORIGINAL: rows were seeded from order
// history joined to the catalog, and the Store column was a per-product store
// select with "Buy again" stacked below it (F6/O3). CURRENT: a row is one
// server-built OWNED ITEM (GET /me/owned-products). The select is retired (F11):
// the Owned column shows the units and, for a member who manages stores, the
// per-store split ("Store A · 2", "Unassigned · 4"); "Buy again" is its own
// cell and adds the product with NO store (F20: a split row has no single
// store). The ⋯ menu hands the item's `lines` to the return dialog so the
// customer can pick WHICH purchase to return (replaces the old browser-side order-line lookup).

import { useEmbedTokens, EmbedBadge, EmbedButton, EmbedStatusBadge, EmbedTable, EmbedText, EmbedActionMenu } from "../../embed-shared/primitives";
import type { EmbedMenuItem } from "../../embed-shared/primitives/EmbedActionMenu";
import type { EmbedTableColumn } from "../../embed-shared/primitives/EmbedTable";
import { OPEN_RMA_STATUSES } from "./rma-api";
import { returnableLines } from "./RmaRequestControl";
import { typeLabel } from "./ProductCard";
import type { OwnedItemWire, OwnedLineWire, OwnedProductRefWire, ServiceOptionWire } from "./owned-products-api";
import { ServiceOptionBadges } from "./ServiceOptionBadges";
import { SubscriptionRowLink } from "./SubscriptionRowLink";

/** Per-product add-to-cart state — moved here from ProductsTab (type-only
 *  importer there) so both tables can render the Buy again control without
 *  importing the tab (a component cycle). Shape unchanged. */
export interface BuyAgainState {
  status: "idle" | "loading" | "success" | "error";
  error?: string;
}

/** A one-time owned item (`kind: "one_time"`); the partition is made in ProductsTab. */
export interface OwnedProductsTableRow {
  item: OwnedItemWire;
}

export interface OwnedProductsTableProps {
  rows: OwnedProductsTableRow[];
  buyAgainStateFor: (productId: string) => BuyAgainState | undefined;
  onBuyAgain: (productId: string) => void;
  /** The product's return-request status to surface — open ?? latest ?? null
   *  (ProductsPanel's derivation). */
  rmaStatusFor: (productId: string) => string | null;
  /** `lines` are the item's source lines (newest first) the return dialog
   *  offers as the purchase to return. */
  onRequestReturn: (productId: string, trigger: HTMLElement, lines: OwnedLineWire[]) => void;
  /** Open the owned-item detail dialog (TD-671 Task 13). */
  onOpenDetails: (item: OwnedItemWire, trigger: HTMLElement) => void;
  /** Open the Move dialog (TD-671 Task 17, R3/R5). Offered only to a member who
   *  manages stores, on a row whose `assignments` are visible and with units owned. */
  onMove?: (item: OwnedItemWire, trigger: HTMLElement) => void;
  /** TD-583: false when the member lacks manage-stores -- the per-store split
   *  is not shown (the server also sends `assignments: null`). Default true. */
  storesPermitted?: boolean;
  /** TD-583: false when the member lacks manage-rma -- the Status and
   *  return-action columns are not rendered. Default true. */
  returnsPermitted?: boolean;
}

/** The row's ⋯ cell, shared with SubscriptionsTable. Amendment: the menu
 *  carries ONLY "Request a return", it renders ONLY for hardware rows (a
 *  return is a physical-goods concept — software/service rows render no menu
 *  at all), and it disappears entirely while an OPEN request exists (the
 *  Status badge then answers "what happened to my return"). Cancel and Buy
 *  again are deliberately NOT menu items — Cancel subscription is the link in
 *  the Details dialog (R9) and Resume a visible row link; Buy again is the
 *  visible button in its own cell. */
export function ProductRowActions({
  productId,
  productName,
  productType,
  lines,
  rmaStatus,
  onRequestReturn,
}: {
  productId: string;
  productName: string;
  /** The product's raw `product_type` — null (the catalog row is gone)
   *  renders no menu: a return needs a known physical product. */
  productType: string | null | undefined;
  lines: OwnedLineWire[];
  rmaStatus: string | null;
  onRequestReturn: (productId: string, trigger: HTMLElement, lines: OwnedLineWire[]) => void;
}) {
  if (productType !== "hardware") return null;
  if (rmaStatus != null && OPEN_RMA_STATUSES.has(rmaStatus)) return null;
  // R8: nothing shipped/delivered with units left means nothing to return; offering
  // the item would open a dialog that can only say it found no purchase.
  if (returnableLines(lines).length === 0) return null;
  const items: EmbedMenuItem[] = [
    {
      id: "request-return",
      label: "Request a return",
      onSelect: (trigger) => onRequestReturn(productId, trigger, lines),
    },
  ];
  return <EmbedActionMenu label={`Actions for ${productName}`} items={items} />;
}

/** The visible "Buy again" cell both tables render. It adds ONE unit of the
 *  product with no store (F20), and acknowledges / surfaces the server's
 *  error beneath the button. */
export function BuyAgainCell({
  productId,
  buyAgainStateFor,
  onBuyAgain,
}: {
  productId: string;
  buyAgainStateFor: (productId: string) => BuyAgainState | undefined;
  onBuyAgain: (productId: string) => void;
}) {
  const t = useEmbedTokens();
  const state = buyAgainStateFor(productId);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs, alignItems: "flex-start", minWidth: 96 }}>
      <EmbedButton
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onBuyAgain(productId)}
        loading={state?.status === "loading"}
        disabled={state?.status === "loading"}
      >
        {state?.status === "loading" ? "Adding..." : "Buy again"}
      </EmbedButton>
      {state?.status === "success" && (
        <EmbedText tone="secondary" size="xs">
          Added to cart
        </EmbedText>
      )}
      {state?.status === "error" && state.error && (
        <EmbedText tone="error" size="xs">
          {state.error}
        </EmbedText>
      )}
    </div>
  );
}

/** The row's thumbnail: product.image_url when the item carries one, NOTHING
 *  otherwise (no grey placeholder — amendment F6). Shared with SubscriptionsTable. */
export function ProductThumbnail({ product }: { product: OwnedProductRefWire }) {
  const t = useEmbedTokens();
  if (!product.image_url) return null;
  return (
    <img
      src={product.image_url}
      alt={product.name}
      style={{
        width: 40,
        height: 40,
        flex: "0 0 auto",
        borderRadius: t.borderRadiusSm,
        objectFit: "cover",
        display: "block",
      }}
    />
  );
}

/** The Product cell both tables render: thumbnail, name and the type badge
 *  (omitted when the catalog row is gone and the type is null). */
export function OwnedProductCell({
  product,
  serviceOptions,
}: {
  product: OwnedProductRefWire;
  /** The item's R9 service options (app badges, auto-install note); rendered
   *  under the name, nothing at all when there are none. */
  serviceOptions?: ServiceOptionWire[];
}) {
  const t = useEmbedTokens();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, minWidth: 0 }}>
      <ProductThumbnail product={product} />
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.xs, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, flexWrap: "wrap", minWidth: 0 }}>
          <span style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>{product.name}</span>
          {product.product_type && <EmbedBadge tone="neutral">{typeLabel(product.product_type)}</EmbedBadge>}
        </div>
        <ServiceOptionBadges options={serviceOptions} />
      </div>
    </div>
  );
}

/** Units owned, and — when the member manages stores and any are assigned — the
 *  split: each store with units, then the unassigned remainder. */
function OwnedQuantityCell({
  item,
  showSplit,
  onMove,
}: {
  item: OwnedItemWire;
  showSplit: boolean;
  onMove?: (item: OwnedItemWire, trigger: HTMLElement) => void;
}) {
  const t = useEmbedTokens();
  const assignments = showSplit ? (item.assignments ?? []) : [];
  const unassigned = item.unassigned_quantity ?? 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span data-testid="owned-quantity" style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>
        {item.quantity_owned ?? 0}
      </span>
      {assignments.length > 0 && (
        <>
          {assignments.map((a) => (
            <EmbedText key={a.store.id} tone="secondary" size="xs">{`${a.store.name} · ${a.quantity}`}</EmbedText>
          ))}
          {unassigned > 0 && (
            <EmbedText tone="secondary" size="xs">{`Unassigned · ${unassigned}`}</EmbedText>
          )}
        </>
      )}
      {showSplit && onMove && item.assignments != null && (item.quantity_owned ?? 0) > 0 && (
        <div style={{ marginTop: 2 }}>
          <EmbedButton
            type="button"
            variant="secondary"
            size="sm"
            aria-label={`Move ${item.product.name} units`}
            onClick={(e) => onMove(item, e.currentTarget)}
          >
            Move
          </EmbedButton>
        </div>
      )}
    </div>
  );
}

export function OwnedProductsTable({
  rows,
  buyAgainStateFor,
  onBuyAgain,
  rmaStatusFor,
  onRequestReturn,
  onOpenDetails,
  onMove,
  storesPermitted = true,
  returnsPermitted = true,
}: OwnedProductsTableProps) {
  return (
    <EmbedTable<OwnedProductsTableRow>
      caption="Hardware and one-time purchases"
      rows={rows}
      rowKey={(r) => r.item.product.id}
      rowTestId={(r) => `owned-row-${r.item.product.id}`}
      columns={[
        {
          id: "product",
          header: "Product",
          primary: true,
          cell: (r) => <OwnedProductCell product={r.item.product} serviceOptions={r.item.service_options} />,
        },
        {
          id: "owned",
          header: "Owned",
          cell: (r) => <OwnedQuantityCell item={r.item} showSplit={storesPermitted} onMove={onMove} />,
        },
        {
          id: "buyAgain",
          header: "",
          stackedLabel: false,
          cell: (r) => (
            <BuyAgainCell productId={r.item.product.id} buyAgainStateFor={buyAgainStateFor} onBuyAgain={onBuyAgain} />
          ),
        },
        {
          id: "details",
          header: "",
          stackedLabel: false,
          cell: (r) => (
            <SubscriptionRowLink
              label="Details"
              ariaLabel={`Details for ${r.item.product.name}`}
              onClick={(trigger) => onOpenDetails(r.item, trigger)}
            />
          ),
        },
        // TD-583: both remaining columns exist only to show / act on RMA data, so
        // a member without manage-rma gets neither (no column of blanks).
        ...(returnsPermitted
          ? ([
              {
                id: "status",
                header: "Status",
                cell: (r: OwnedProductsTableRow) => (
                  <EmbedStatusBadge domain="rma" value={rmaStatusFor(r.item.product.id) ?? undefined} />
                ),
              },
              {
                id: "actions",
                header: "",
                stackedLabel: false,
                cell: (r: OwnedProductsTableRow) => (
                  <ProductRowActions
                    productId={r.item.product.id}
                    productName={r.item.product.name}
                    productType={r.item.product.product_type}
                    lines={r.item.lines}
                    rmaStatus={rmaStatusFor(r.item.product.id)}
                    onRequestReturn={onRequestReturn}
                  />
                ),
              },
            ] as EmbedTableColumn<OwnedProductsTableRow>[])
          : []),
      ]}
    />
  );
}
